import { useParams } from "react-router-dom";
import { ConfirmCancelButtons } from "@components/Buttons/ConfirmCancelButtons";
import WarningModal from "@components/Modals/WarningModal";
import messages from "@constants/Messages";
import LoadingIcon from "@common/LoadingIcon";

import { ContactBasicInfoCard } from "@components/Forms/ContactProfileForm/components/BasicInfoCard";
import { ContactCompanyInfoCard } from "@components/Forms/ContactProfileForm/components/ContactCompanyInfoCard";
import { ContactRegistrationInfoCard } from "@components/Forms/ContactProfileForm/components/ContactRegistrationCard";
import { useContactProfileForm } from "@components/Forms/ContactProfileForm/hooks/useContactProfileForm";
import DuplicateUserModal from "@components/Modals/DuplicateUsersModal";

type ContactProfileFormProps = {
  id?: string;
};

const ContactProfileForm = ({
  id: propId,
}: ContactProfileFormProps): React.JSX.Element => {
  const { id: paramId } = useParams<{ id: string }>();
  const effectiveId = propId || paramId;

  const form = useContactProfileForm(effectiveId);

  return (
    <div>
      {form.loading || form.uploadCardsLoading ? (
        <div className="flex justify-center items-center min-h-screen">
          <LoadingIcon />
        </div>
      ) : form.userNotFound ? (
        <p className="not-found-text">
          ユーザーが見つかりませんでした（ID: {effectiveId}）
        </p>
      ) : (
        <form
          onSubmit={
            form.createExternalContact || form.createInternalContact
              ? form.handleCreateNewUser
              : form.handleUpdateUser
          }
          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
        >
          <div className="flex flex-row justify-end">
            {form.error && (
              <button
                className="button-default"
                type="button"
                onClick={form.handleOpenWarningModal}
              >
                {messages.generic.show_error}
              </button>
            )}
          </div>

          <ContactBasicInfoCard
            contactData={form.contactData}
            loading={form.loading}
            isViewingHistory={form.isViewingHistory}
            createContact={form.createContact}
            files={form.files}
            setFiles={form.setFiles}
            setBusinessCards={form.setBusinessCards}
            setImageFileIndexes={form.setImageFileIndexes}
            contactHistoryData={form.contactHistoryData}
            selectedOption={form.selectedOption}
            handleDropdownChange={form.handleDropdownChange}
            updateUserData={form.updateUserData}
            updateError={form.updateError}
            errors={form.errors}
          />

          <ContactCompanyInfoCard
            contactData={form.contactData}
            updateUserData={form.updateUserData}
            errors={form.errors}
            updateError={form.updateError}
            isViewingHistory={form.isViewingHistory}
          />

          <ContactRegistrationInfoCard
            contactData={form.contactData}
            isViewingHistory={form.isViewingHistory}
            createContact={form.createContact}
            implementerLogin={form.implementerLogin}
          />

          <WarningModal
            title={messages.generic.error_modal_title}
            body={form.error}
            confirmText={""}
            cancelText="戻る"
            isOpen={form.warningModalOpen}
            onRequestClose={form.handleCloseWarningModal}
          />

          <DuplicateUserModal
            title={`${form.dupErrorType}が重複しています。`}
            type={form.dupErrorType}
            isOpen={form.duplicateExistsModal}
            onClose={() => form.setDuplicateExistsModal(false)}
            duplicateUsers={form.duplicateUsers}
            onCreateUser={(e) => form.handleCreateNewUser(e, true)}
            onUpdateUser={(e, userId) => form.handleUpdateUser(e, userId, true)}
          />

          {!form.isViewingHistory && (
            <ConfirmCancelButtons
              confirmText={
                form.createInternalContact || form.createExternalContact
                  ? "作成"
                  : "更新"
              }
              form={true}
              disabled={form.loading}
              onCancel={form.onCancel}
            />
          )}
        </form>
      )}
    </div>
  );
};

export default ContactProfileForm;
